import { observer } from 'mobx-react';
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';

import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';
import { analytics } from '../../analytics';
import { breakpoints, navigation } from '../../constants';
import { Button as UnstyledButton, HeaderContainer } from './styles';
import { Button } from '../button/new';
import { Flex } from '../grid';
import { Icon } from '../icons';
import { Link } from '../link';
import { Logo } from '../logo';
import { MobileNavigation } from './navigation';
import { Profile } from './profile';
import { profileDefaultProps, profilePropTypes } from '../../prop-types';
import { useFeature } from '../../hooks/use-feature';
import { GLOBAL_BANNER_HEIGHT } from '../surprise-and-delight/global-banner';

const StyledLogo = styled(Logo)`
  height: 22px;

  @media (min-width: ${breakpoints.lg}) {
    height: 32px;
  }
`;

const MobileHeader = ({ display, profile, handleSignOut, dark }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { MenuStore } = useRootStore();
  const { isCateringMenu } = MenuStore;

  const cateringEnabled = useFeature('catering-enabled');
  const showCatering = cateringEnabled && isCateringMenu;
  const buttonText = showCatering ? 'Order Catering' : 'Order Food';
  const eventText = showCatering ? 'Catering Menu' : 'Menu';
  const routeTo = showCatering ? navigation.CATERING : navigation.MENU;

  return (
    <HeaderContainer display={display} color={dark ? 'white' : 'black'}>
      <Flex flexShrink={1} alignItems="center">
        <Link href="/">
          <StyledLogo />
        </Link>
      </Flex>
      <Flex alignItems="center" justifyContent="flex-end" flexGrow={1} gap={{ xs: '12px', lg: '24px' }}>
        <Button
          as="a"
          href={routeTo}
          widthType="fit"
          size="medium"
          display={{ xs: 'none', lg: 'inline-flex' }}
          onClick={() =>
            analytics.track(ANALYTICS_EVENTS.HEADER_NAV_ITEM_CLICKED, {
              text: eventText,
            })
          }
        >
          {buttonText}
        </Button>
        <Profile profile={profile} handleSignOut={handleSignOut} dark={dark} />
        <UnstyledButton
          type="button"
          title="menu"
          onClick={() => {
            setIsOpen(true);
          }}
        >
          <Icon
            name="hamburger"
            width={{ xs: '32px', lg: '36px' }}
            height={{ xs: '32px', lg: '36px' }}
            fill={dark ? 'white' : 'black'}
          />
        </UnstyledButton>
        <MobileNavigation
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
          }}
        />
      </Flex>
    </HeaderContainer>
  );
};

const headerAnimationVariants = {
  offscreen: {
    y: '-100%',
  },
  onscreen: {
    y: 0,
    transition: {
      duration: 0.6,
      delay: 0,
      ease: 'easeOut',
    },
  },
};

const Header = ({ profile, handleSignOut, enableAnimation, dark, deliveryBannerActive, topBannerActive }) => {
  // The header is absolutely positioned so it overlays page heroes. When a
  // top-of-page banner is present (delivery in-progress and/or the Surprise &
  // Delight promo strip), shift the header down by each banner's height so it
  // sits below them rather than overlapping.
  const topOffset = (deliveryBannerActive ? 56 : 0) + (topBannerActive ? GLOBAL_BANNER_HEIGHT : 0);

  return (
    <motion.div
      initial={enableAnimation ? 'offscreen' : 'onscreen'}
      animate="onscreen"
      variants={enableAnimation ? headerAnimationVariants : {}}
      style={{
        position: 'absolute',
        top: topOffset ? `${topOffset}px` : 0,
        left: 0,
        right: 0,
        zIndex: 1000,
      }}
    >
      <ComposedMobileHeader
        profile={profile}
        handleSignOut={handleSignOut}
        dark={dark}
        deliveryBannerActive={deliveryBannerActive}
      />
    </motion.div>
  );
};

MobileHeader.propTypes = profilePropTypes;
MobileHeader.defaultProps = profileDefaultProps;

const ComposedMobileHeader = observer(MobileHeader);

Header.propTypes = {
  ...profilePropTypes,
  enableAnimation: PropTypes.bool,
  deliveryBannerActive: PropTypes.bool,
  topBannerActive: PropTypes.bool,
};

Header.defaultProps = {
  ...profileDefaultProps,
  enableAnimation: false,
  deliveryBannerActive: false,
  topBannerActive: false,
};

export { Header };
